/* eslint-disable max-lines */
import {makeAutoObservable, observable} from 'mobx'
import {
  ControlledProcess,
  ffLazyLoading,
  hasFeatureFlag,
  HTMLUtil,
  logger,
  ObjUtil,
  processController,
  Scheduler,
  SearchUtil,
  Time,
  TypeUtil
} from 'yougile-util'
import {
  ChatMessageJSON,
  DataObjectId,
  getChecklists,
  getChecklistsAsText,
  GroupChat,
  Hub,
  noBoardContainerId,
  SandboxTree,
  StorageDepartmentHierarchy,
  User
} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {DepartmentsClient} from '@client/departments-client'
import {DirectMessages} from '@client/direct-messages'
import {GroupChatsClient} from '@client/group-chats-client'
import {getLazySearchResults} from '@client/lazy-load'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {FilePreview} from '@common/file-preview'
import {Tracking, TrackingEvent} from '@common/tracking'
import {idTasksApi} from '@extensions/id-tasks/id-tasks-store'
import {ChatUtils} from './chat-utils'
import {globals} from './globals-base'
import {MessageUtils} from './message-utils'
import {searchProgress} from './search-progress'

export const MessageTimeIdThreshold = 10000

export type SearchResultItemType =
  | 'message'
  | 'file'
  | 'user-name'
  | 'group-chat-title'
  | 'task-title'
  | 'description'
  | 'checklist-title'
  | 'checklist-item'
  | 'id'

interface SearchItem {
  id: PackedId
  text: string
}

export interface SearchResultItem<T = User | Hub> {
  entity: T
  messageTime?: number
  scoreTime: number
  score: number
  group: number
  start: number
  end: number
  type: SearchResultItemType
  chatId: string
  searchQuery: string
  messageData?: ChatMessageJSON
}

export type FilterFn = (item: SearchResultItem) => boolean

export type PackedId = string & {__type: 'Packed'}

interface ScoredPackedId {
  id: PackedId
  score: number
  start: number
  end: number
  group: number
}

const packId = (
  id: DataObjectId,
  type: SearchResultItemType,
  messageTime?: number
) => {
  return (id +
    ',' +
    type +
    (messageTime !== undefined ? ',' + messageTime : '')) as PackedId
}

export const unpackId = (id: PackedId) => {
  const parts = id.split(',')
  return {
    id: parts[0] as DataObjectId,
    type: parts[1] as SearchResultItemType,
    messageTime: +parts[2] ? +parts[2] : undefined
  }
}

const cmpScore = (a: SearchResultItem, b: SearchResultItem): number =>
  a.group - b.group || b.scoreTime - a.scoreTime

interface SearchIndex {
  [id: PackedId]: string
}

const withLazyTasks = hasFeatureFlag(ffLazyLoading)

export class Search {
  private readonly companyId: DataObjectId
  private readonly index: SearchIndex = {}

  constructor(companyId: DataObjectId) {
    this.companyId = companyId
    const box = Sync.get({companyId})
    if (!box) {
      return
    }
    if (!withLazyTasks) {
      SyncEvents.addListener(companyId, '*', res => {
        res.forEach(up => {
          up.ids.forEach(id => this.update(id))
        })
      })
      SyncEvents.addListener(Sync.profileId, '*', res => {
        res.forEach(up => {
          up.ids.forEach(id => {
            const obj = Sync.getProfile().get(id)
            if (obj instanceof Hub) {
              const u = DirectMessages.getDirectChatUser(obj)
              if (u && box.isUserInCompany(u.id)) {
                this.update(u.id)
              }
            }
          })
        })
      })
    }
  }

  public static calcHighlights(
    query: string,
    text: string
  ): Array<[number, number]> {
    if (!query) {
      return []
    }
    const cleanText = SearchUtil.cleanStr(text)
    const cleanQuery = SearchUtil.cleanStr(query)
    const parts = SearchUtil.prepareWords(cleanQuery)

    const highlighted: Array<[number, number]> = []
    parts.forEach(p => {
      if (!p) {
        return
      }
      const isStop = SearchUtil.isStopWord(p)
      let pos = 0
      let ix = -1
      do {
        ix = cleanText.indexOf(p, pos)
        if (ix > -1) {
          if (!isStop || SearchUtil.isCleanMatch(cleanText, p, ix)) {
            highlighted.push([ix, ix + p.length - 1])
          }
          pos = ix + p.length
        }
      } while (ix > -1)
    })
    highlighted.sort((a, b) => a[0] - b[0])
    return highlighted
  }

  public static highlight(
    query: string,
    text: string,
    eachText: (t: string, isFirst: boolean, isLast: boolean) => void,
    eachHighlight: (t: string) => void
  ): void {
    const highlighted = Search.calcHighlights(query, text)
    if (highlighted.length === 0) {
      eachText(text, true, true)
      return
    }
    let curr = 0
    highlighted.forEach(h => {
      if (h[0] < curr) {
        return
      }
      if (h[0] - curr > 0) {
        const plain = text.slice(curr, h[0])
        eachText(plain, curr === 0, false)
      }
      const hl = text.slice(h[0], h[1] + 1)
      eachHighlight(hl)
      curr = h[1] + 1
    })
    if (curr < text.length) {
      const plain = text.slice(curr, text.length)
      eachText(plain, false, true)
    }
  }

  private findIds(query: string, limit?: number): ScoredPackedId[] {
    query = SearchUtil.cleanStr(query)
    const words = SearchUtil.prepareWords(query)
    if (!words || words.length === 0) {
      return []
    }

    // Allow no more than 30 symbols inbetween words in total
    const distance = words.reduce((a, w) => a + w.length, 30)

    // Filter results so that they contain only results with valid word groups
    const found: ScoredPackedId[] = []
    for (const id of ObjUtil.keys(this.index)) {
      const str = this.index[id]
      const res = SearchUtil.hasAllWordsInVicinity(str, words, distance)
      if (res) {
        const [start, end] = res
        const group = this.idToGroup(id)
        found.push({id, score: 0, start, end, group})

        if (typeof limit !== 'undefined' && found.length > limit) {
          break
        }
      }
    }
    return found
  }

  private async findIdsLong(
    process: ControlledProcess,
    query: string,
    limit?: number
  ): Promise<ScoredPackedId[]> {
    query = SearchUtil.cleanStr(query)
    const words = SearchUtil.prepareWords(query)
    if (!words || words.length === 0) {
      return []
    }

    // Allow no more than 30 symbols inbetween words in total
    const distance = words.reduce((a, w) => a + w.length, 30)

    // Filter results so that they contain only results with valid word groups
    const found: ScoredPackedId[] = []
    for (const id of ObjUtil.keys(this.index)) {
      await process.checkpoint()

      const str = this.index[id]
      const res = SearchUtil.hasAllWordsInVicinity(str, words, distance)
      if (res) {
        const [start, end] = res
        const group = this.idToGroup(id)
        found.push({id, score: 0, start, end, group})

        if (typeof limit !== 'undefined' && found.length > limit) {
          break
        }
      }
    }
    return found
  }

  private idToGroup(id: PackedId): number {
    const {type} = unpackId(id)
    switch (type) {
      case 'id':
        return 0
      case 'user-name':
        return 1
      case 'group-chat-title':
        return 2
      default:
        return 3
    }
  }

  /**
   * @deprecated Use searchLong
   */
  public search(query: string, limit?: number): SearchResultItem[] {
    const box = Sync.get({companyId: this.companyId})
    if (!box) {
      return []
    }
    const hierarchy = DepartmentsClient.getHierarchy(box)

    const idResults = this.getIdTasksResults(query, box, hierarchy)
    const results = this.findIds(query, limit)
      .map(item => this.prepareItem(item, box, hierarchy, query))
      .filter(TypeUtil.isTruthy)
      .sort(cmpScore)

    return ([] as SearchResultItem[]).concat(idResults, results)
  }

  public async searchLong(
    process: ControlledProcess,
    query: string,
    limit?: number,
    forceNoLimit = false
  ): Promise<{
    result: SearchResultItem[]
    totalCount: number
  }> {
    const withLazyTasks = hasFeatureFlag(ffLazyLoading)
    if (withLazyTasks) {
      const lazySearchResult = await getLazySearchResults(query, forceNoLimit)
      return lazySearchResult
    }

    const box = Sync.get({companyId: this.companyId})
    if (!box) {
      return {
        result: [],
        totalCount: 0
      }
    }

    const hierarchy = DepartmentsClient.getHierarchy(box)
    const results: SearchResultItem[] = this.getIdTasksResults(
      query,
      box,
      hierarchy
    )
    const packedIds = await this.findIdsLong(process, query, limit)
    for (const packedId of packedIds) {
      await process.checkpoint()
      const item = this.prepareItem(packedId, box, hierarchy, query)
      item && results.push(item)
    }
    results.sort(cmpScore)
    return {
      result: results,
      totalCount: results.length
    }
  }

  private prepareItem(
    item: ScoredPackedId,
    box: SandboxTree,
    hierarchy: StorageDepartmentHierarchy,
    query: string
  ): SearchResultItem | undefined {
    const {id} = unpackId(item.id)
    const entity = box.get(id)
    if (entity instanceof Hub) {
      return this.prepareHub(item, entity, box, hierarchy, query)
    } else if (entity instanceof User) {
      return this.prepareUser(item, entity, query)
    } else {
      return undefined
    }
  }

  private prepareHub(
    item: ScoredPackedId,
    entity: Hub,
    box: SandboxTree,
    hierarchy: StorageDepartmentHierarchy,
    query: string
  ): SearchResultItem<Hub> | undefined {
    const {messageTime, type} = unpackId(item.id)
    const hub = entity
    if (
      box.isDeleted(hub.id) ||
      hub.isHidden() ||
      !PermissionsChecker.checkShow(hierarchy, hub, {})
    ) {
      return undefined
    }
    const lastMessageTime = hub.chat?.messages?.size
      ? Math.max(...hub.chat.messages.keys())
      : hub.timestamp
    let scoreTime = lastMessageTime
    if (type === 'message') {
      scoreTime = messageTime ?? lastMessageTime
    }
    return {
      entity: hub,
      type,
      messageTime,
      scoreTime,
      score: item.score,
      group: item.group,
      start: item.start,
      end: item.end,
      chatId: item.id,
      searchQuery: query
    } as SearchResultItem<Hub>
  }

  private prepareUser(
    item: ScoredPackedId,
    entity: User,
    query: string
  ): SearchResultItem<User> | undefined {
    const {messageTime, type} = unpackId(item.id)
    const user = entity
    const hub = DirectMessages.getChatIfExists(user.id)
    const messages = hub?.chat?.messages
    const lastMessageTime = messages?.size
      ? Math.max(...messages.keys())
      : hub?.timestamp ?? user.timestamp
    return {
      entity: user,
      type,
      messageTime,
      scoreTime: messageTime ?? lastMessageTime,
      score: item.score,
      group: item.group,
      start: item.start,
      end: item.end,
      chatId: item.id,
      searchQuery: query
    }
  }

  private add(item: SearchItem): void {
    if (!item || JSON.stringify(item).length > 100_000) {
      logger.log('skipping indexing', item)
      return
    }
    if (item.text) {
      this.index[item.id] = SearchUtil.cleanStr(item.text)
    }
  }

  private updateChat(box: SandboxTree, hub: Hub, userId?: DataObjectId): void {
    hub?.chat?.messages?.forEach(m => {
      if (m?.text && !m.deleted) {
        const id = packId(userId || hub.id, 'message', m.timestamp)
        const text = MessageUtils.renderHtmlForSearch(box, m)

        this.add({id, text})
      }
    })
  }

  private update(id: DataObjectId): void {
    const box = Sync.get({companyId: this.companyId})
    if (!box) {
      return
    }
    const obj = box.get(id)
    if (obj instanceof Hub) {
      if (GroupChat.isGroupChat(obj)) {
        this.updateGroup(box, obj)
      } else if (box.getLevel(obj) === 4) {
        this.updateHub(box, obj)
      }
    } else if (obj instanceof User) {
      this.updateUser(box, obj)
    }
  }

  private updateUser(box: SandboxTree, user: User): void {
    if (!user || user.id === globals.currentUserId) {
      return
    }
    this.add({
      id: packId(user.id, 'user-name'),
      text: Accounts.getName(user.id)
    })
    const hub = DirectMessages.getChatIfExists(user.id)
    this.updateChat(box, hub, user.id)
  }

  private updateGroup(box: SandboxTree, hub: Hub): void {
    if (!hub) {
      return
    }
    this.add({
      id: packId(hub.id, 'group-chat-title'),
      text: GroupChat.getTitle(hub) || this.prepareHubName(hub)
    })

    this.updateChat(box, hub)
  }

  private updateHub(box: SandboxTree, hub: Hub): void {
    if (!hub) {
      return
    }
    this.add({
      id: packId(hub.id, 'task-title'),
      text: this.prepareHubName(hub)
    })

    if (hub.description?.trim()) {
      const html = FilePreview.encodedToFilenames(hub.description)
      const text = HTMLUtil.extractText(html, undefined, true)

      this.add({
        id: packId(hub.id, 'description'),
        text
      })
    }

    const checklists = getChecklists(hub)
    const checklistsAsText = getChecklistsAsText(checklists)
    const type = 'checklist-item'
    const id = packId(hub.id, type)
    this.add({id, text: checklistsAsText})

    this.updateChat(box, hub)
  }

  private prepareHubName(hub: Hub): string {
    return hub.companyId !== Sync.profileId && hub.properties.feedbackUserEmail
      ? '📢 ' + hub.properties.feedbackUserEmail + ' ➡️ ' + hub.title
      : hub.title
  }

  private async doBuildIndex(): Promise<void> {
    const box = Sync.get({companyId: this.companyId})
    if (!box) {
      return
    }
    if (box.companyId === Sync.profileId) {
      return
    }

    box.getAllUsers(false).forEach(u => this.updateUser(box, u))

    const allTaskList: Hub[] = []
    ;(GroupChatsClient.listChats(box) || []).forEach(h => allTaskList.push(h))

    const hierarchy = DepartmentsClient.getHierarchy(box)
    box.walkForward(box.getHub(box.companyId), (obj, level) => {
      if (obj.id === noBoardContainerId) {
        return true
      }

      if (obj.isHidden() || obj.deleted) {
        return false
      }

      if (level === 4 || obj.location === noBoardContainerId) {
        if (ChatUtils.isAvailableChat(box, obj.id, hierarchy)) {
          allTaskList.push(obj)
        }
        return false
      }

      return true
    })

    searchProgress.setAllHubs(box.companyId, allTaskList.length)

    const time = (h: Hub): number =>
      (h.chat.lastMessage()?.timestamp ?? h.timestamp) || h.timestamp

    allTaskList.sort((b, a) => time(a) - time(b))

    const count = allTaskList.length

    const title = box.get(box.companyId).title
    const t1 = Date.now()

    const [, process] = processController({
      // 2ms out of 16ms frame time
      scheduler: Scheduler.frame(),
      maxIterationTime: 6
    })

    for (let i = 0; i < allTaskList.length; i++) {
      const hub = allTaskList[i]

      await process.checkpoint()

      this.update(hub.id)

      if (i % 100 === 0 || i === allTaskList.length - 1) {
        searchProgress.setProcessedHubs(box.companyId, i + 1)
      }
    }

    const t2 = Date.now()
    if (t2 - t1 > 2000) {
      logger.log('Done search index', title, count, t2 - t1 + ' ms')
    }
  }

  public buildIndex(): void {
    if (globals.getCompanyId() === this.companyId) {
      searchProgress.addProgress(this.companyId)
      const t1 = Date.now()

      void this.doBuildIndex()
        .then(() => {
          searchProgress.endProgress(this.companyId)
          searchObserver.updateRefreshSearch()
          const t2 = Date.now()
          if (t2 - t1 > Time.minute * 2) {
            void Tracking.send(TrackingEvent.searchBuildIndex, {
              time: Math.floor((t2 - t1) / Time.minute)
            })
          }
        })
        .catch(e => {
          logger.error('Error while building index', e)
        })
    } else {
      const id = globals.onCompanyChange(() => {
        if (globals.getCompanyId() === this.companyId) {
          searchProgress.addProgress(this.companyId)
          const t1 = Date.now()
          globals.removeCompanyListener(id)
          void this.doBuildIndex()
            .then(() => {
              searchProgress.endProgress(this.companyId)
              searchObserver.updateRefreshSearch()
              const t2 = Date.now()
              if (t2 - t1 > Time.minute * 2) {
                void Tracking.send(TrackingEvent.searchBuildIndex, {
                  time: Math.floor((t2 - t1) / Time.minute)
                })
              }
            })
            .catch(e => {
              logger.error('Error while building index', e)
            })
        }
      })
    }
  }

  private getIdTasksResults(
    search: string,
    box: SandboxTree,
    hierarchy: StorageDepartmentHierarchy
  ): SearchResultItem<Hub>[] {
    const taskIds = this.getIdTaskFromSearch(search)
    if (!taskIds?.length) {
      return []
    }

    return taskIds
      .map(taskId => box.getHub(taskId))
      .filter(
        (hub): hub is Hub =>
          !!hub &&
          !box.isDeleted(hub.id) &&
          !hub.isHidden() &&
          PermissionsChecker.checkShow(hierarchy, hub, {})
      )
      .map(entity => ({
        entity,
        type: 'id' as SearchResultItemType,
        messageTime: 0,
        scoreTime: entity.timestamp,
        score: 0,
        group: 0,
        start: 0,
        end: Number.MAX_VALUE,
        chatId: entity.id,
        searchQuery: search
      }))
      .sort(cmpScore)
  }

  private getIdTaskFromSearch(search: string): string[] | undefined {
    // If changed, should change `isIdQuery` too
    search = search.trim()

    const anyMatch = search.match(/^\d+$/gi)
    if (anyMatch?.length) {
      const idTask = anyMatch[0]
      return idTasksApi.getReverseIdTaskForceAny(idTask)
    }

    const exactMatch = search.match(/^\w+-\d+$/)
    if (exactMatch?.length) {
      const [prefix, idTask] = exactMatch[0].split('-')
      const id = idTasksApi.getReverseIdTask(idTask, prefix)
      return id ? [id] : undefined
    }

    search = search.toLowerCase()

    let idsProject = Object.values(idTasksApi.getIdsProject() || {})
    idsProject = idsProject.map(p => p.toLowerCase())
    const taskIds = []

    for (const idProject of idsProject) {
      if (search.startsWith(idProject)) {
        const idTask = search.slice(idProject.length)
        const id = idTasksApi.getReverseIdTask(idTask, idProject)
        if (id) {
          taskIds.push(id)
        }
      }
    }

    return taskIds.length === 0 ? undefined : taskIds
  }

  public static isIdQuery(search: string): boolean {
    search = search.trim()
    const anyMatch = search.match(/^\d+$/gi)
    if (anyMatch?.length) {
      return true
    }
    const exactMatch = search.match(/^\w+-\d+$/)
    if (exactMatch?.length) {
      return true
    }
    const lettersAndDigitsMatch = search.match(/^[\d|\w]+$/gi)
    if (lettersAndDigitsMatch?.length) {
      return true
    }
    return false
  }
}

interface State {
  refreshSearch: number
}

export class SearchObserver {
  state: State = {refreshSearch: 0}
  constructor() {
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
  }
  updateRefreshSearch(): void {
    this.state.refreshSearch = Date.now()
  }
}

export const searchObserver = new SearchObserver()
